import { useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import MerchandiseOfferCarousel from 'components/Merchandise/MerchandiseCarousels/MerchandiseOfferCarousel'
import { MerchandisePointsCarouselCategoryPillsRow } from 'components/Merchandise/MerchandiseCarousels/MerchandisePointsCarouselCategoryPills'
import useMerchandiseCategoriesList from 'hooks/Merchandise/useMerchandiseCategoriesList'
import LoyaltySpendPointsPricingContext from 'loyalty/contexts/LoyaltySpendPointsPricingContext'
import type { SpendPointsSortOption } from './loyaltySpendPointsTypes'
import { MINIMUM_OFFERS_TO_SHOW_CAROUSEL } from './loyaltySpendPointsConstants'

interface Props {
  sortOption: SpendPointsSortOption
}

const SHOP_CATEGORY_FILTERS: App.MerchandiseCategoryFilters = {
  sortBy: 'top',
  level: 0,
  limit: 4,
}
const SORT_BY_OPTION: Record<SpendPointsSortOption, NonNullable<App.MerchandiseFilters['sortBy']>> = {
  recommended: 'recommended',
  'lowest-points': 'priceAsc',
  'highest-points': 'priceDesc',
}

function LoyaltySpendPointsShopCarousel({ sortOption }: Props) {
  const intl = useIntl()
  const { burnPointsLimit, showOffersWithinBalance } = useContext(LoyaltySpendPointsPricingContext)
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null)
  const [categoryIds] = useMerchandiseCategoriesList(SHOP_CATEGORY_FILTERS)

  const filters = useMemo<App.MerchandiseFilters>(() => ({
    sortBy: SORT_BY_OPTION[sortOption],
    ...(selectedCategoryId ? { categories: [selectedCategoryId] } : {}),
    ...(showOffersWithinBalance && burnPointsLimit !== undefined ? { points: { max: burnPointsLimit } } : {}),
  }), [burnPointsLimit, selectedCategoryId, showOffersWithinBalance, sortOption])

  useEffect(() => {
    if (selectedCategoryId && !categoryIds.includes(selectedCategoryId)) {
      setSelectedCategoryId(null)
    }
  }, [categoryIds, selectedCategoryId])

  const onCategoryChange = useCallback((categoryId: string | null) => {
    setSelectedCategoryId(categoryId)
  }, [])

  const categoryPills = useMemo(() => <MerchandisePointsCarouselCategoryPillsRow
    categoryIds={categoryIds}
    selectedCategoryId={selectedCategoryId}
    onSelectCategory={onCategoryChange}
    allCategoriesLabel={intl.formatMessage({
      id: 'dveUfxvVXUn1WGpPNaH8',
      defaultMessage: 'All Categories',
      description: 'All categories filter pill for shop offers on the Societe spend points page',
    })}
  />, [categoryIds, intl, onCategoryChange, selectedCategoryId])

  return <MerchandiseOfferCarousel
    title={intl.formatMessage({
      id: '1LIz8Kt_QskiyIZazwze',
      defaultMessage: '<i>Shop</i> offers',
      description: 'Heading for shop offers carousel on the spend points page',
    }, {
      i: (chunks: ReactNode) => <i>{chunks}</i>,
    })}
    filters={filters}
    belowTitle={categoryPills}
    viewAllTo="/shop"
    trackingListName="Spend my Points Shop"
    trackingPageName="Societe Spend my Points"
    minimumOffersToShow={showOffersWithinBalance ? MINIMUM_OFFERS_TO_SHOW_CAROUSEL : undefined}
  />
}

export default LoyaltySpendPointsShopCarousel
